import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarPlus, Check } from "lucide-react";
import { API, Shell, useT } from "./Shell";
import "./testTheme.css";

const gcalLink = (booking, duration) => {
  const start = new Date(`${booking.date}T${booking.slot}:00+05:30`);
  const end = new Date(start.getTime() + duration * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent("Physics Consultation & Viva — Pranjal Sir Physics")}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(`Booking ${booking.booking_id} · One-to-one with Pranjal Dwivedi Sir (online)`)}`;
};

export default function TestBooking() {
  const { studentId } = useParams();
  const T = useT();
  const [status, setStatus] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [message, setMessage] = useState("");
  const [upiId, setUpiId] = useState("");
  const [upiConsent, setUpiConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/test/status/${studentId}`).then(({ data }) => setStatus(data)).catch(() => setError("notfound"));
  }, [studentId]);

  useEffect(() => {
    if (!date) return;
    setSlot("");
    setSlots(null);
    axios.get(`${API}/test/slots`, { params: { date } }).then(({ data }) => setSlots(data.slots)).catch(() => setSlots([]));
  }, [date]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/test/book`, { student_id: studentId, date, slot, difficulty, message, upi_id: upiId, upi_consent: upiConsent });
      setConfirmed(data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T.l_err_generic);
    } finally { setLoading(false); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const student = status?.student;

  return (
    <Shell>
      <div className="tp-wrap" style={{ maxWidth: 680, padding: "50px 22px 70px" }}>
        {confirmed ? (
          <div className="tp-panel" style={{ textAlign: "center" }} data-testid="tp-booking-confirmed">
            <div style={{ width: 54, height: 54, borderRadius: "50%", background: "#ecfdf3", color: "#12b76a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><Check size={26} /></div>
            <h2 className="tp-h2">{T.b_confirmed}</h2>
            <div className="tp-detail-grid" style={{ textAlign: "left" }}>
              <div>{T.b_ref}<b data-testid="tp-booking-ref">{confirmed.booking.booking_id}</b></div>
              <div>{T.b_date2}<b>{confirmed.booking.date}</b></div>
              <div>{T.b_time}<b>{confirmed.booking.slot} IST</b></div>
              <div>{T.b_dur}<b>{confirmed.consult_duration} {T.b_dur_val}</b></div>
            </div>
            <p className="tp-muted" style={{ fontSize: 12 }}>{T.b_note}</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 16 }}>
              <a className="tp-btn secondary small" href={gcalLink(confirmed.booking, confirmed.consult_duration)} target="_blank" rel="noreferrer" data-testid="tp-gcal"><CalendarPlus size={15} /> {T.b_gcal}</a>
              <a className="tp-btn ghost small" href={`/physics-test/status/${studentId}`}>{T.b_viewstatus}</a>
            </div>
          </div>
        ) : (
          <div className="tp-panel">
            <div className="tp-eyebrow">{T.b_eyebrow}</div>
            <h2 className="tp-h2">{T.b_title}</h2>
            {student && <div className="tp-detail-grid">
              <div>{T.b_student}<b>{student.name}</b></div>
              <div>{T.b_sid}<b>{student.student_id}</b></div>
              <div>{T.b_wa}<b>{student.whatsapp}</b></div>
              <div>{T.b_exam}<b>{student.target_exam}</b></div>
            </div>}
            {error && <p className="tp-error" role="alert" data-testid="tp-book-error">{error === "notfound" ? T.s_notfound : error}</p>}
            <form onSubmit={submit} data-testid="tp-book-form">
              <label className="tp-label">{T.b_date}<input type="date" required min={today} max={maxDate} value={date} onChange={(e) => setDate(e.target.value)} data-testid="tp-book-date" /></label>
              {date && slots !== null && (
                <div style={{ marginBottom: 14 }}>
                  <span className="tp-label" style={{ marginBottom: 8 }}>{T.b_slots}</span>
                  {slots.length === 0 ? <p className="tp-note">{T.b_noslots}</p> : (
                    <div className="tp-slots" data-testid="tp-slots">{slots.map((s) => <button type="button" key={s} className={slot === s ? "tp-slot selected" : "tp-slot"} onClick={() => setSlot(s)} data-testid={`tp-slot-${s.replace(":", "")}`}>{s}</button>)}</div>
                  )}
                </div>
              )}
              <label className="tp-label">{T.b_diff}<select required value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="tp-book-difficulty"><option value="">{T.b_diff_sel}</option>{T.b_diffs.map((d) => <option key={d}>{d}</option>)}</select></label>
              <label className="tp-label">{T.b_msg}<textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={T.b_msg_ph} data-testid="tp-book-message" /></label>
              <label className="tp-label">{T.b_upi}<input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder={T.b_upi_ph} data-testid="tp-book-upi" /></label>
              {upiId.trim() && <label className="tp-check"><input type="checkbox" checked={upiConsent} onChange={(e) => setUpiConsent(e.target.checked)} data-testid="tp-book-upi-consent" /> {T.b_upi_consent}</label>}
              <button className="tp-btn" style={{ width: "100%" }} disabled={loading || !slot} type="submit" data-testid="tp-book-submit">{loading ? T.b_submitting : T.b_submit}</button>
            </form>
          </div>
        )}
      </div>
    </Shell>
  );
}
